// History of standalone passports (W2 / W4) — list, re-print, edit, delete.
// Re-print re-enqueues a passport print job (kind='passport'); the PDF is
// downloaded from print history once the agent renders it.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import { request } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

interface PassportRow {
  id: string;
  doc_number: string;
  article: string | null;
  rfid_tag: string | null;
  test_data: Record<string, unknown>;
  meta: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

interface Page<T> {
  items: T[];
  pagination: { page: number; page_size: number; total: number; pages: number };
}

export default function PassportHistory() {
  return (
    <RequirePermission codes="document.passport.manage">
      <Inner />
    </RequirePermission>
  );
}

function Inner() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");

  const list = useQuery({
    queryKey: ["admin", "passports", "list", page, q],
    queryFn: () =>
      request<Page<PassportRow>>(
        `/admin/passports?page=${page}&page_size=50${q ? `&q=${encodeURIComponent(q)}` : ""}`,
      ),
  });

  const del = useMutation({
    mutationFn: (id: string) => request<void>(`/admin/passports/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "passports"] }),
  });

  const reprint = useMutation({
    mutationFn: (id: string) =>
      request<{ print_job: { job_id: string } }>(`/admin/passports/${id}/print`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
    onSuccess: (res) => alert(t("passportHistory.enqueued", { job: res.print_job?.job_id ?? "—" })),
  });

  return (
    <>
      <PageHeader
        eyebrow={t("eyebrows.adminPrint")}
        title={t("passportHistory.title")}
        description={t("passportHistory.description")}
        actions={
          <Link to="/admin/print/passports" className="btn btn-primary">
            {t("passportHistory.newPassport")}
          </Link>
        }
      />

      <div className="card p-3 mt-3 flex items-center gap-2">
        <input
          className="flex-1 px-3 py-2 rounded-md bg-surface border border-border text-sm"
          placeholder={t("passportHistory.searchPlaceholder")}
          value={q}
          onChange={(e) => {
            setPage(1);
            setQ(e.target.value);
          }}
        />
      </div>

      {list.isLoading && <LoadingState />}
      {list.isError && <ErrorState error={list.error} />}

      {list.data && list.data.items.length === 0 && (
        <div className="card p-8 mt-4 text-center text-text-muted">
          {t("passportHistory.empty")}
        </div>
      )}

      {list.data && list.data.items.length > 0 && (
        <section className="card mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt text-text-muted">
              <tr>
                <th className="px-3 py-2 text-left">{t("passportHistory.colDocNumber")}</th>
                <th className="px-3 py-2 text-left">{t("passportHistory.colArticle")}</th>
                <th className="px-3 py-2 text-left">{t("passportHistory.colRfid")}</th>
                <th className="px-3 py-2 text-right">{t("passportHistory.colMetrics")}</th>
                <th className="px-3 py-2 text-left">{t("common.created")}</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2 font-mono text-xs">{r.doc_number}</td>
                  <td className="px-3 py-2">{r.article ?? "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.rfid_tag ?? "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">
                    {Object.keys(r.test_data ?? {}).length}
                  </td>
                  <td className="px-3 py-2 text-text-muted text-xs font-mono">
                    {formatDateTime(r.created_at)}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      className="btn btn-sm"
                      onClick={() => reprint.mutate(r.id)}
                      disabled={reprint.isPending}
                    >
                      {t("passportHistory.print")}
                    </button>{" "}
                    <Link to={`/admin/print/passports?from=${r.id}`} className="btn btn-sm">
                      {t("passportHistory.editAction")}
                    </Link>{" "}
                    <button
                      className="btn btn-sm"
                      onClick={() => {
                        if (confirm(t("passportHistory.confirmDelete", { doc: r.doc_number }))) {
                          del.mutate(r.id);
                        }
                      }}
                      disabled={del.isPending}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {del.isError && (
        <ErrorBanner onDismiss={() => del.reset()}>
          {(del.error as Error).message}
        </ErrorBanner>
      )}

      {list.data && list.data.pagination.pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-text-muted">
            {t("common.pageOf", {
              page: list.data.pagination.page,
              pages: list.data.pagination.pages,
              total: list.data.pagination.total,
            })}
          </span>
          <div className="flex gap-2">
            <button className="btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              ←
            </button>
            <button
              className="btn"
              disabled={page >= list.data.pagination.pages}
              onClick={() => setPage((p) => p + 1)}
            >
              →
            </button>
          </div>
        </div>
      )}
    </>
  );
}
